import { apiClient } from './client';
import { getCsrfToken } from './auth';

// Types
export interface JamesThread {
  id: string;
  userId: string;
  title: string | null;
  status: 'active' | 'archived';
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  messages?: JamesMessage[];
}

export interface JamesMessage {
  id: string;
  threadId: string;
  role: 'user' | 'assistant';
  content: string;
  toolCalls?: JamesToolCallReference[];
  inputTokens?: number;
  outputTokens?: number;
  createdAt: string;
}

export interface JamesToolCallReference {
  id: string;
  name: string;
  arguments: Record<string, unknown>;
}

export interface JamesToolCall {
  id: string;
  messageId: string;
  toolName: string;
  arguments: Record<string, unknown>;
  status: 'pending' | 'running' | 'completed' | 'failed';
  result?: unknown;
  error?: string;
  durationMs?: number;
  createdAt: string;
  completedAt?: string;
}

// SSE Event types
export interface JamesSSEEvent {
  type: 'thread_created' | 'text_delta' | 'tool_use_start' | 'tool_use_result' | 'message_complete' | 'error';
  data: unknown;
}

export interface JamesTextDeltaEvent {
  text: string;
}

export interface JamesToolUseStartEvent {
  id: string;
  name: string;
}

export interface JamesToolUseResultEvent {
  id: string;
  name: string;
  result: unknown;
}

export interface JamesMessageCompleteEvent {
  content: string;
  inputTokens: number;
  outputTokens: number;
}

export interface JamesErrorEvent {
  message: string;
}

export interface JamesAskUserEvent {
  id: string;
  question: string;
  options: Array<{ label: string; description?: string }>;
}

export interface JamesThreadCreatedEvent {
  threadId: string;
}

// API functions
export async function getThreads(): Promise<{ threads: JamesThread[] }> {
  return apiClient.get('/agent/threads');
}

export async function getThread(id: string): Promise<{ thread: JamesThread }> {
  return apiClient.get(`/agent/threads/${id}`);
}

export async function createThread(data?: {
  title?: string;
  metadata?: Record<string, unknown>;
}): Promise<{ thread: JamesThread }> {
  return apiClient.post('/agent/threads', data ?? {});
}

export async function deleteThread(id: string): Promise<{ success: boolean }> {
  return apiClient.delete(`/agent/threads/${id}`);
}

// SSE streaming helpers
export interface StreamMessageOptions {
  onThreadCreated?: (threadId: string) => void;
  onTextDelta?: (text: string) => void;
  onToolUseStart?: (data: JamesToolUseStartEvent) => void;
  onToolUseResult?: (data: JamesToolUseResultEvent) => void;
  onMessageComplete?: (data: JamesMessageCompleteEvent) => void;
  onAskUser?: (data: JamesAskUserEvent) => void;
  onError?: (error: string) => void;
}

async function doFetch(
  url: string,
  body: { content: string },
  csrfToken: string | null,
  signal?: AbortSignal
): Promise<Response> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (csrfToken) {
    headers['X-CSRF-Token'] = csrfToken;
  }

  return fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
    credentials: 'include',
    signal,
  });
}

async function streamResponse(
  url: string,
  body: { content: string },
  options: StreamMessageOptions,
  csrfToken: string | null,
  signal?: AbortSignal
): Promise<void> {
  let response = await doFetch(url, body, csrfToken, signal);

  // If CSRF token is stale, refresh and retry once
  if (response.status === 403) {
    try {
      const freshToken = await getCsrfToken();
      response = await doFetch(url, body, freshToken, signal);
    } catch {
      // If refresh fails, throw the original 403
    }
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: { message: 'Request failed' } }));
    throw new Error(error.error?.message ?? 'Request failed');
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('No response body');
  }

  const decoder = new TextDecoder();
  let buffer = '';
  let receivedComplete = false;
  let receivedError = false;

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Parse SSE events from buffer
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? ''; // Keep incomplete line in buffer

      let currentEvent: string | null = null;
      let currentData: string | null = null;

      for (const line of lines) {
        if (line.startsWith('event: ')) {
          currentEvent = line.slice(7);
        } else if (line.startsWith('data: ')) {
          currentData = line.slice(6);
        } else if (line === '' && currentEvent && currentData) {
          // End of event, process it
          try {
            const data = JSON.parse(currentData);
            if (currentEvent === 'message_complete') receivedComplete = true;
            if (currentEvent === 'error') receivedError = true;
            handleSSEEvent(currentEvent, data, options);
          } catch {
            console.warn('[James SSE] Failed to parse event data', { event: currentEvent, data: currentData });
            if (currentEvent === 'error') {
              receivedError = true;
              options.onError?.('Received a malformed error response from the server.');
            }
          }
          currentEvent = null;
          currentData = null;
        }
      }
    }

    // If the stream ended without a message_complete or error event,
    // the connection was likely dropped (server timeout, network issue).
    if (!receivedComplete && !receivedError) {
      options.onError?.('Connection lost — James may still be processing. Please try again.');
    }
  } catch (err) {
    // AbortError means the user intentionally stopped the stream — not an error
    if (err instanceof DOMException && err.name === 'AbortError') {
      return;
    }
    throw err;
  } finally {
    reader.releaseLock();
  }
}

function handleSSEEvent(
  eventType: string,
  data: unknown,
  options: StreamMessageOptions
): void {
  switch (eventType) {
    case 'thread_created':
      options.onThreadCreated?.((data as JamesThreadCreatedEvent).threadId);
      break;
    case 'text_delta':
      options.onTextDelta?.((data as JamesTextDeltaEvent).text);
      break;
    case 'tool_use_start':
      options.onToolUseStart?.(data as JamesToolUseStartEvent);
      break;
    case 'tool_use_result':
      options.onToolUseResult?.(data as JamesToolUseResultEvent);
      break;
    case 'message_complete':
      options.onMessageComplete?.(data as JamesMessageCompleteEvent);
      break;
    case 'ask_user':
      options.onAskUser?.(data as JamesAskUserEvent);
      break;
    case 'error':
      options.onError?.((data as JamesErrorEvent).message);
      break;
    default:
      console.warn('[James SSE] Unrecognized event type', eventType);
      break;
  }
}

export async function sendMessage(
  threadId: string,
  content: string,
  options: StreamMessageOptions,
  signal?: AbortSignal
): Promise<void> {
  const csrfToken = apiClient.getCsrfToken();
  return streamResponse(
    `/api/agent/threads/${threadId}/messages`,
    { content },
    options,
    csrfToken,
    signal
  );
}

export async function quickChat(
  content: string,
  options: StreamMessageOptions,
  signal?: AbortSignal
): Promise<void> {
  const csrfToken = apiClient.getCsrfToken();
  return streamResponse(
    '/api/agent/chat',
    { content },
    options,
    csrfToken,
    signal
  );
}
